/**
 * Business Settings Page - Geschäftsstatus-Einstellungen
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  Button,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import {
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
  DoNotDisturb as DoNotDisturbIcon,
} from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import { BusinessStatusInfo, BusinessStatusMode } from "../../types/api-types";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import OpeningHoursSettings from "../../components/Business/OpeningHoursSettings";
import HolidaysSettings from "../../components/Business/HolidaysSettings";
import PhoneOffsetSettings from "../../components/Business/PhoneOffsetSettings";

const BusinessSettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [businessStatus, setBusinessStatus] =
    useState<BusinessStatusInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Load business status
  const loadBusinessStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await apiClient.getBusinessStatus();
      setBusinessStatus(status);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden des Geschäftsstatus");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinessStatus();
  }, []);

  const handleStatusChange = async (status: BusinessStatusMode) => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.setBusinessStatus({ status });
      await loadBusinessStatus();
    } catch (err: any) {
      setError(err.message || "Fehler beim Speichern des Geschäftsstatus");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("de-DE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const effectiveStatus =
    businessStatus?.status ||
    (businessStatus?.isOpen ? BusinessStatusMode.OPEN : BusinessStatusMode.CLOSED);
  const statusLabel =
    effectiveStatus === BusinessStatusMode.OPEN
      ? "Geöffnet"
      : effectiveStatus === BusinessStatusMode.REQUEST
        ? "Auf Anfrage geöffnet"
        : "Geschlossen";
  const statusColor =
    effectiveStatus === BusinessStatusMode.OPEN
      ? "success.main"
      : effectiveStatus === BusinessStatusMode.REQUEST
        ? "warning.main"
        : "error.main";
  const statusOptions = [
    {
      value: BusinessStatusMode.OPEN,
      label: "Geöffnet",
      description: "Sofort als geöffnet anzeigen.",
      icon: <CheckCircleIcon />,
      color: "success.main",
      bg: "success.50",
    },
    {
      value: BusinessStatusMode.CLOSED,
      label: "Geschlossen",
      description: "Sofort als geschlossen anzeigen.",
      icon: <DoNotDisturbIcon />,
      color: "error.main",
      bg: "error.50",
    },
    {
      value: BusinessStatusMode.REQUEST,
      label: "Auf Anfrage",
      description: "Termine nur nach Rücksprache.",
      icon: <HelpOutlineIcon />,
      color: "warning.main",
      bg: "warning.50",
    },
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return <LoadingSpinner text="Lade Geschäftsdaten..." />;
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <BusinessIcon fontSize={isMobile ? "medium" : "large"} />
              Geschäftseinstellungen
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Verwalten Sie Status, Öffnungszeiten und Feiertage
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 64,
              textTransform: "none",
              fontSize: "1rem",
            },
          }}
        >
          <Tab icon={<BusinessIcon />} label="Status" iconPosition="start" />
          <Tab
            icon={<AccessTimeIcon />}
            label="Öffnungszeiten"
            iconPosition="start"
          />
          <Tab icon={<EventIcon />} label="Feiertage" iconPosition="start" />
          <Tab
            icon={<InfoIcon />}
            label="Telefon-Offsets"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Current Status Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight="bold"
                    color="primary"
                  >
                    Standard-Öffnungsstatus
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        bgcolor: statusColor,
                      }}
                    />
                    <Typography variant="h6" fontWeight="bold">
                      {statusLabel}
                    </Typography>
                    <Chip
                      size="small"
                      label={saving ? "Wird gespeichert..." : "Aktiv"}
                      color={saving ? "default" : "primary"}
                      variant={saving ? "outlined" : "filled"}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Dieser Status wird oben in der Anwendung und öffentlich
                    verwendet. Wähle direkt aus, ob die Werkstatt geöffnet,
                    geschlossen oder nur auf Anfrage verfügbar ist.
                  </Typography>

                  {businessStatus?.nextOpenTime && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Nächste Öffnung: {formatDate(businessStatus.nextOpenTime)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Status Settings */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="bold"
                      color="primary"
                    >
                      Status auswählen
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    {statusOptions.map((option) => {
                      const selected = effectiveStatus === option.value;
                      return (
                        <Grid item xs={12} md={4} key={option.value}>
                          <Button
                            fullWidth
                            variant={selected ? "contained" : "outlined"}
                            disabled={saving}
                            onClick={() => handleStatusChange(option.value)}
                            sx={{
                              minHeight: 132,
                              alignItems: "flex-start",
                              justifyContent: "flex-start",
                              textAlign: "left",
                              p: 2,
                              borderRadius: 2,
                              borderColor: selected
                                ? option.color
                                : "divider",
                              bgcolor: selected ? option.color : "background.paper",
                              color: selected ? "white" : "text.primary",
                              "&:hover": {
                                bgcolor: selected ? option.color : option.bg,
                                borderColor: option.color,
                              },
                            }}
                          >
                            <Stack spacing={1} alignItems="flex-start">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: selected ? "white" : option.color,
                                }}
                              >
                                {option.icon}
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {option.label}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: selected
                                    ? "rgba(255,255,255,0.85)"
                                    : "text.secondary",
                                  textTransform: "none",
                                }}
                              >
                                {option.description}
                              </Typography>
                            </Stack>
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                    Kein Grund nötig: Der ausgewählte Status wird direkt
                    gespeichert und ersetzt die alte Grund/Status-Einstellung.
                  </Alert>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && <OpeningHoursSettings />}
        {activeTab === 2 && <HolidaysSettings />}
        {activeTab === 3 && <PhoneOffsetSettings />}
      </Box>
    </Container>
  );
};

export default BusinessSettingsPage;
